import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import produce from 'immer'

import { CheckRequiredFields } from '../../../../../../components/CheckRequiredFields/CheckRequiredFields'
import { useToastContext } from '../../../../../../contexts'
import {
  DespatchAdvice,
  DespatchAdviceItem,
  PatchRequest,
  WebEdiDto,
} from '../../../../../../data/models'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { useConfigContext } from '../../../../../ui-config'
import {
  requestCompleteDespatchAdviceById,
  useAllDespatchAdviceItems,
  useDespatchAdviceById,
} from '../../../../data'
import { AcceptView } from './Accept.view'

type Props = {
  id: string
}

export const AcceptContainer: React.FC<Props> = ({ id }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isVisible, setIsVisible] = useState(false)

  const { data: despatchAdvice } = useDespatchAdviceById(id)
  const { data: despatchAdviceItems, isLoading: isDespatchAdviceItemsLoading } =
    useAllDespatchAdviceItems(id)

  const { uiConfig: config } = useConfigContext()
  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const currentDate = new Date()

  const prepareCompletePatchDto = (
    newDespatchAdvice: WebEdiDto<DespatchAdvice>,
    oldDespatchAdvice: WebEdiDto<DespatchAdvice>,
  ): PatchRequest<WebEdiDto<DespatchAdvice>> => {
    const patchDtos: PatchRequest<WebEdiDto<DespatchAdvice>> = []

    // ConfirmationCode
    patchDtos.push({
      op: oldDespatchAdvice.Payload.ConfirmationCode ? 'replace' : 'add',
      path: '/Payload/ConfirmationCode',
      value: newDespatchAdvice.Payload.ConfirmationCode,
    })

    // ConfirmationDate
    if (
      newDespatchAdvice.Payload.ConfirmationDate !==
      oldDespatchAdvice.Payload.ConfirmationDate
    ) {
      patchDtos.push({
        op: oldDespatchAdvice.Payload.ConfirmationDate ? 'replace' : 'add',
        path: '/Payload/ConfirmationDate',
        value: newDespatchAdvice.Payload.ConfirmationDate,
      })
    }

    // deliveryNoteDate
    if (
      newDespatchAdvice.Payload.DeliveryNoteDate !==
      oldDespatchAdvice.Payload.DeliveryNoteDate
    ) {
      patchDtos.push({
        op: oldDespatchAdvice.Payload.DeliveryNoteDate ? 'replace' : 'add',
        path: '/Payload/DeliveryNoteDate',
        value: newDespatchAdvice.Payload.DeliveryNoteDate,
      })
    }

    return patchDtos
  }

  async function handleSubmit() {
    // deactivate the buttons
    setIsRequestRunning(true)

    try {
      // close the dialog and reset
      setIsVisible(false)

      // submit form
      await submitForm()

      // navigate back to the start page
      navigate('/')

      // show success toast
      showToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t(
          'despatchAdvice.title.send.dialog.send.messages.success.summary',
        ),
        detail: t(
          'despatchAdvice.title.send.dialog.send.messages.success.detail',
          {
            orderId: despatchAdvice?.Payload.OrderId,
          },
        ),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t(
          'despatchAdvice.title.send.dialog.send.messages.failure.detail',
          {
            orderId: despatchAdvice?.Payload.OrderId,
          },
        ),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  // submit form
  const submitForm = () => {
    // make a copy of despatchAdvice
    const newDespatchAdvice = produce(despatchAdvice!, draft => {
      draft.Payload.ConfirmationDate = new Date().toISOString() // set date to now

      draft.Payload.DeliveryNoteDate = currentDate.toISOString()

      // check if an item was modified or rejected
      const isItemModified = (item: DespatchAdviceItem) =>
        item.ConfirmationCode === 'MODIFIED' ||
        item.ConfirmationCode === 'REJECTED'

      draft.Payload.ConfirmationCode = despatchAdviceItems?.some(isItemModified)
        ? 'MODIFIED'
        : 'ACCEPTED'
    })

    // patch for DespatchAdvice
    const response = requestCompleteDespatchAdviceById(
      id,
      prepareCompletePatchDto(newDespatchAdvice, despatchAdvice!),
    )

    // return response header
    return response
  }

  const handleClose = () => {
    setIsVisible(false)
  }

  return (
    <CheckRequiredFields data={despatchAdvice}>
      {(isDisabled, tooltip) => (
        <AcceptView
          config={config}
          despatchAdvice={despatchAdvice}
          isRequestRunning={isRequestRunning}
          despatchAdviceItems={despatchAdviceItems}
          isDespatchAdviceItemsLoading={isDespatchAdviceItemsLoading}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          currentDate={currentDate}
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          isDisabled={isDisabled}
          tooltip={tooltip}
        />
      )}
    </CheckRequiredFields>
  )
}
